import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 15;
const MAX_SIZE = 12 * 1024 * 1024;

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/") || /heic|heif/i.test(file.type)) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 1800 / Math.max(img.width, img.height));
      if (scale === 1 && file.size < 1.5 * 1024 * 1024) return resolve(file);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file),
        "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

export const PhotoUpload = ({ photos, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).slice(0, MAX_FILES - photos.length);
    if (files.length === 0) {
      toast.error(`Je kunt maximaal ${MAX_FILES} foto's toevoegen.`);
      return;
    }
    setUploading((n) => n + files.length);
    const uploaded = [];
    for (const original of files) {
      try {
        const file = await compressImage(original);
        if (file.size > MAX_SIZE) throw new Error("Bestand is te groot (max. 12 MB).");
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/uploads`, fd);
        uploaded.push({ ...data, preview: URL.createObjectURL(file) });
      } catch (err) {
        toast.error("Foto uploaden mislukt", {
          description: err?.response?.data?.detail || err.message || `${original.name} kon niet worden geüpload.`,
        });
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (uploaded.length) onChange([...photos, ...uploaded]);
  };

  const remove = (id) => onChange(photos.filter((p) => p.id !== id));

  return (
    <div data-testid="photo-upload">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {photos.map((p) => (
          <div key={p.id} data-testid="photo-upload-item" className="group relative aspect-square overflow-hidden border border-[#D8D6D0] bg-white">
            <img src={p.preview} alt={p.original_filename} className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid="photo-upload-remove"
              onClick={() => remove(p.id)}
              aria-label="Foto verwijderen"
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-[#111111] text-white transition-colors duration-200 hover:bg-cream hover:text-[#111111]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {Array.from({ length: uploading }).map((_, i) => (
          <div key={`up-${i}`} className="flex aspect-square items-center justify-center border border-dashed border-[#D8D6D0] bg-white text-[#8E8D8A]">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ))}
        {photos.length + uploading < MAX_FILES && (
          <button
            type="button"
            data-testid="photo-upload-button"
            onClick={() => inputRef.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1.5 border border-dashed border-[#B9B7B0] bg-white text-[#555555] transition-colors duration-200 hover:border-[#111111] hover:text-[#111111]"
          >
            <Camera className="h-5 w-5" />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.15em]">Foto toevoegen</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        data-testid="photo-upload-input"
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <p className="mt-2 text-xs text-[#8E8D8A]">
        Maximaal {MAX_FILES} foto's (JPG, PNG of HEIC). Een foto van de huidige situatie helpt mij om een
        nauwkeurige offerte te maken.
      </p>
    </div>
  );
};
